<script setup lang="ts">
import { ref, onMounted, inject, computed, Ref } from 'vue'
import { useRouter } from 'vue-router'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'vue-sonner'
import { useApplications } from '@/services/Applications'
import { Member } from '@/types/Member'
import {
	Trophy, Plus, MoreHorizontal, Search, Calendar,
	Eye, Settings2, Copy, Trash2, Loader2, Rocket
} from 'lucide-vue-next'

const router = useRouter()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)
const { getApplicationList } = useApplications()

const contests = ref<any[]>([])
const loading = ref(true)
const searchQuery = ref('')
const showCreateDialog = ref(false)

const newContest = ref({
	name: '',
	description: '',
	type: 'contest',
})

const filteredContests = computed(() => {
	if (!searchQuery.value) return contests.value
	const q = searchQuery.value.toLowerCase()
	return contests.value.filter(c =>
		(c.name || '').toLowerCase().includes(q) ||
		(c.description || '').toLowerCase().includes(q)
	)
})

const getStatusBadge = (contest: any) => {
	if (contest.status === 'active' || contest.moderationstatus === 1) return { label: 'Active', variant: 'default' as const }
	if (contest.status === 'draft' || contest.moderationstatus === 0) return { label: 'Draft', variant: 'secondary' as const }
	if (contest.status === 'ended' || contest.moderationstatus === 2) return { label: 'Ended', variant: 'outline' as const }
	return { label: 'Draft', variant: 'secondary' as const }
}

const formatDate = (dateStr?: string) => {
	if (!dateStr) return '—'
	try {
		const d = new Date(dateStr)
		return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
	} catch { return dateStr }
}

const fetchContests = async () => {
	if (!vhostId?.value) return
	loading.value = true
	try {
		// Contests use application list filtered by type
		const apps = await getApplicationList(vhostId.value)
		contests.value = apps.filter((a: any) => a.type === 'contest' || a.type === 'campaign') || []
		if (contests.value.length === 0) {
			// If none found, show all apps as fallback
			contests.value = apps || []
		}
	} catch (err) {
		console.error('Failed to load contests:', err)
	} finally {
		loading.value = false
	}
}

const handleCreate = async () => {
	if (!newContest.value.name) {
		toast.error('Please enter a name')
		return
	}
	toast.success('Contest creation started — configure it in the wizard.')
	showCreateDialog.value = false
	newContest.value = { name: '', description: '', type: 'contest' }
}

const navigateToContest = (contest: any) => {
	router.push({ name: 'Application', params: { appId: contest.id } })
}

const duplicateContest = (contest: any) => {
	toast.info(`Duplicating "${contest.name}"...`)
}

const deleteContest = (contest: any) => {
	toast.error(`Delete "${contest.name}" — not yet implemented`)
}

onMounted(fetchContests)
</script>

<template>
	<div class="p-3 md:p-6 space-y-4">
		<!-- Header -->
		<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
			<div>
				<h2 class="text-2xl font-light">Contests & Campaigns</h2>
				<p class="text-sm text-muted-foreground">Create and manage interactive contests, campaigns, and engagement activities.</p>
			</div>
			<Button class="gap-2 shrink-0" @click="showCreateDialog = true">
				<Plus class="h-4 w-4" /> New Contest
			</Button>
		</div>

		<!-- Search bar -->
		<div class="relative max-w-sm">
			<Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
			<Input v-model="searchQuery" placeholder="Search contests..." class="pl-9" />
		</div>

		<!-- Loading state -->
		<div v-if="loading" class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
			<Card v-for="n in 6" :key="n">
				<CardContent class="p-4 space-y-3">
					<Skeleton class="h-5 w-3/4" />
					<Skeleton class="h-4 w-1/2" />
					<Skeleton class="h-20 w-full" />
				</CardContent>
			</Card>
		</div>

		<!-- Empty state -->
		<div v-else-if="filteredContests.length === 0" class="flex flex-col items-center justify-center py-20 gap-4">
			<div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
				<Trophy class="h-8 w-8 text-muted-foreground" />
			</div>
			<div class="text-center">
				<p class="text-lg font-medium">No contests found</p>
				<p class="text-sm text-muted-foreground mt-1">Create your first contest to start engaging users.</p>
			</div>
			<Button @click="showCreateDialog = true" class="gap-2">
				<Plus class="h-4 w-4" /> Create Contest
			</Button>
		</div>

		<!-- Contest grid -->
		<div v-else class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
			<Card
				v-for="contest in filteredContests"
				:key="contest.id"
				class="cursor-pointer hover:border-primary/40 hover:shadow-md transition-all group"
				@click="navigateToContest(contest)"
			>
				<CardHeader class="flex flex-row items-start justify-between p-4 pb-2">
					<div class="flex items-center gap-3 min-w-0">
						<div class="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
							<Trophy class="h-5 w-5 text-primary" />
						</div>
						<div class="min-w-0">
							<CardTitle class="text-sm font-medium group-hover:text-primary transition-colors truncate">{{ contest.name }}</CardTitle>
							<CardDescription class="text-xs truncate">{{ contest.maindomain || contest.description || 'No description' }}</CardDescription>
						</div>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger as-child>
							<Button variant="ghost" size="icon" class="h-8 w-8 shrink-0" @click.stop>
								<MoreHorizontal class="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuItem @click.stop="navigateToContest(contest)" class="gap-2">
								<Settings2 class="h-4 w-4" /> Edit
							</DropdownMenuItem>
							<DropdownMenuItem @click.stop class="gap-2">
								<Eye class="h-4 w-4" /> Preview
							</DropdownMenuItem>
							<DropdownMenuItem @click.stop="duplicateContest(contest)" class="gap-2">
								<Copy class="h-4 w-4" /> Duplicate
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem @click.stop="deleteContest(contest)" class="text-destructive gap-2">
								<Trash2 class="h-4 w-4" /> Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</CardHeader>
				<CardContent class="px-4 pb-4 pt-0">
					<div class="flex items-center gap-3 mt-2">
						<Badge :variant="getStatusBadge(contest).variant" class="text-[10px]">{{ getStatusBadge(contest).label }}</Badge>
						<span v-if="contest.created" class="text-xs text-muted-foreground flex items-center gap-1">
							<Calendar class="h-3 w-3" /> {{ formatDate(contest.created) }}
						</span>
					</div>
				</CardContent>
			</Card>
		</div>

		<!-- Create Contest Dialog -->
		<Dialog v-model:open="showCreateDialog">
			<DialogContent class="sm:max-w-md">
				<DialogHeader>
					<DialogTitle class="flex items-center gap-2">
						<Rocket class="h-5 w-5 text-primary" />
						Create New Contest
					</DialogTitle>
					<DialogDescription>Set up a new contest or campaign for your audience.</DialogDescription>
				</DialogHeader>
				<div class="space-y-4 py-4">
					<div class="space-y-2">
						<Label>Name</Label>
						<Input v-model="newContest.name" placeholder="My Awesome Contest" />
					</div>
					<div class="space-y-2">
						<Label>Description</Label>
						<Textarea v-model="newContest.description" placeholder="Brief description..." rows="3" />
					</div>
					<div class="space-y-2">
						<Label>Type</Label>
						<Select v-model="newContest.type">
							<SelectTrigger>
								<SelectValue placeholder="Select type" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="contest">Contest</SelectItem>
								<SelectItem value="campaign">Campaign</SelectItem>
								<SelectItem value="sweepstakes">Sweepstakes</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
					<Button @click="handleCreate" :disabled="!newContest.name" class="gap-2">
						<Plus class="h-4 w-4" /> Create
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>
